import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
} from '@mui/material';

import { ConvertTOPdf } from '@app/utils';
import { Strings } from '@app/constants';
import { FormProvider } from '../hook-form';

interface IExportColumn {
  key: string;
  label: string;
}

interface IExportedData {
  tableHeadCsv: IExportColumn[];
  rows: Array<Record<string, any>>;
  csvName: string;
  exportedTo: string;
}

interface IProps {
  exportDialogOpen: boolean;
  handleClose: () => void;
  exportedData: IExportedData;
}

const FieldToExportDialog = ({ exportDialogOpen, handleClose, exportedData }: IProps) => {
  const methods = useForm<Record<string, boolean>>({
    defaultValues: exportedData?.tableHeadCsv?.reduce<Record<string, boolean>>((acc, col) => {
      acc[col.key] = true;
      return acc;
    }, {}),
  });

  const { handleSubmit, control } = methods;

  const onSubmit = (form: any) => {
    const selectedFields = Object.entries(form)
      .filter(([_, checked]) => checked)
      .map(([key]) => key);

    const filteredColumns = exportedData.tableHeadCsv.filter((col) =>
      selectedFields.includes(col.key)
    );
    const filteredRows = exportedData.rows.map((row) => {
      const filteredRow: Record<string, any> = {};
      selectedFields.forEach((key) => {
        filteredRow[key] = row[key];
      });
      return filteredRow;
    });

    if (exportedData?.exportedTo === 'pdf') {
      ConvertTOPdf(filteredRows, filteredColumns, 'landscape', exportedData.csvName);
    } else {
      const csvContent = {
        data: filteredRows,
        headers: filteredColumns.map((col) => ({
          label: col.label,
          key: col.key,
        })),
        filename: `${exportedData.csvName}.csv`,
      };

      const csvLink = document.createElement('a');
      const blob = new Blob(
        [
          [
            csvContent.headers.map((h) => `"${h.label}"`).join(','),
            ...csvContent.data.map((row) =>
              csvContent.headers
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                .map((h: { label: string; key: string }) => `"${row[h.key] ?? ''}"`)
                .join(',')
            ),
          ].join('\n'),
        ],
        { type: 'text/csv;charset=utf-8;' }
      );
      csvLink.href = URL.createObjectURL(blob);
      csvLink.setAttribute('download', csvContent.filename);
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
    }

    handleClose();
  };

  return (
    <Dialog open={exportDialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{Strings.eta.selectField}</DialogTitle>
      <FormProvider methods={methods}>
        <DialogContent dividers>
          <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1} ml={2} mt={2}>
            {exportedData?.tableHeadCsv?.length > 0 &&
              exportedData?.tableHeadCsv.map((col) => (
                <Controller
                  key={col.key}
                  name={col.key}
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={col.label}
                    />
                  )}
                />
              ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {Strings.button.cancel}
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={Object.values(methods.watch()).filter(Boolean).length === 0}
            onClick={handleSubmit(onSubmit)}
          >
            {Strings.button.save}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export { FieldToExportDialog };
